// /consent-print — Printable blank consent letter for Let Go Wellness (Connecticut).
// Optimized for one-click Print → Save as PDF, no digital signing.

import React from "react";
import { Link } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";

const CONSENT_SECTIONS = [
  {
    title: "Nature of services",
    body:
      "Let Go Wellness provides outpatient mental-health services including individual, couples, family, and group therapy delivered in-person and via secure telehealth. Sessions are typically 50 minutes; couples and initial evaluations may run 80 minutes. Care is delivered by licensed clinicians and, at times, by supervised trainees under a licensed clinical supervisor. There are no guarantees regarding treatment outcomes. Therapy may bring up strong emotions, memories, or discomfort as part of the healing process.",
  },
  {
    title: "Confidentiality & its legal limits",
    body:
      "All communication and records are confidential and protected under HIPAA (45 CFR §§ 160 & 164) and Connecticut General Statutes §§ 52-146c through 52-146j (psychologist–patient and mental-health-provider–patient privilege). Your clinician is required by Connecticut law and professional ethics to break confidentiality in the following circumstances: (1) reasonable suspicion of abuse or neglect of a child (Conn. Gen. Stat. § 17a-101), elder (Conn. Gen. Stat. § 17b-451), or dependent adult; (2) an imminent, serious threat of harm to yourself or another identifiable person; (3) a court order compelling disclosure. Consultation with fellow clinicians and clinical supervisors regarding your care is standard practice and remains confidential.",
  },
  {
    title: "Fees, payment & insurance",
    body:
      "Session fees are due at the time of service. You are financially responsible for services rendered regardless of insurance status. If Let Go Wellness is in-network with your insurance, we bill directly and you are responsible for co-pays, deductibles, and any denied claims. If you are self-pay or out-of-network, a superbill can be provided on request. Returned-check and outstanding-balance fees may apply. Under the federal No Surprises Act, you have the right to receive a Good Faith Estimate of expected charges for out-of-network care — ask us for one at any time.",
  },
  {
    title: "Cancellation policy",
    body:
      "We ask for at least 24 hours' notice for cancellations. Sessions cancelled with less than 24 hours' notice, or missed without notice, are billed at the full session fee. Insurance does not reimburse for late cancellations. Repeated no-shows may result in discharge from services.",
  },
  {
    title: "Communication & telehealth",
    body:
      "Email, text messaging, and standard phone are not fully secure and are used only for scheduling and brief logistics — never for clinical content. For clinical exchange we use a HIPAA-compliant telehealth and messaging platform. Telehealth sessions require a private space and a reliable internet connection; both patient and clinician must be physically located in the State of Connecticut at the time of the session unless otherwise agreed, in accordance with Conn. Gen. Stat. § 19a-906 (telehealth).",
  },
  {
    title: "Emergencies & after-hours care",
    body:
      "Let Go Wellness is not an emergency service and does not provide 24-hour crisis coverage. If you are in crisis and cannot wait for a scheduled appointment, call 988 (Suicide & Crisis Lifeline), 911, or go to your nearest emergency room. Connecticut residents may also contact the Mobile Crisis Intervention Services by dialing 211. You may leave a non-urgent voicemail; we return calls within one business day.",
  },
  {
    title: "Client rights",
    body:
      "You have the right to ask questions about your treatment, refuse or discontinue services at any time, request a copy of your record, and request a second opinion or transfer to another provider. Complaints regarding care may be submitted to the Connecticut Department of Public Health, Practitioner Licensing and Investigations Section, 410 Capitol Ave., MS #12APP, Hartford, CT 06134, phone (860) 509-7603.",
  },
  {
    title: "Consent",
    body:
      "By signing below, I acknowledge that I have read, understood, and had opportunity to ask questions about the terms above, and I consent to receive mental-health services from Let Go Wellness. If the patient is a minor (under 18) or under conservatorship, a parent, legal guardian, or conservator must also sign.",
  },
];

const FieldLine = ({ label, span = 1 }) => (
  <div className={span === 2 ? "col-span-2" : ""}>
    <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-700 mb-2">
      {label}
    </div>
    <div className="border-b border-neutral-800 h-6" />
  </div>
);

export default function ConsentPrint() {
  const handlePrint = () => window.print();

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #ffffff !important; }
          .print-page {
            padding: 0.5in 0.6in !important;
            box-shadow: none !important;
            border: none !important;
            margin: 0 !important;
            max-width: none !important;
          }
          .print-section { break-inside: avoid; }
          /* Hide the global floating widgets that render outside Routes */
          [data-testid="call-button"],
          [data-testid="background-music"],
          [data-testid="floating-call-button"],
          [data-testid="floating-music-player"],
          [data-testid="music-player"],
          .fixed { display: none !important; }
        }
        @page {
          size: Letter;
          margin: 0.4in;
        }
      `}</style>

      <div className="min-h-screen bg-neutral-100 py-10 print:bg-white print:py-0">
        <div className="no-print max-w-[8.5in] mx-auto flex items-center justify-between mb-6 px-4">
          <Link
            to="/"
            data-testid="consent-print-back"
            className="inline-flex items-center gap-2 text-sm text-neutral-700 hover:text-neutral-900"
          >
            <ArrowLeft className="w-4 h-4" /> Back to site
          </Link>
          <button
            onClick={handlePrint}
            data-testid="consent-print-button"
            className="inline-flex items-center gap-2 bg-[#3A5A40] text-white px-6 py-3 text-sm tracking-wide hover:bg-[#2c4530]"
          >
            <Printer className="w-4 h-4" /> Print / Save as PDF
          </button>
        </div>

        <article
          className="print-page bg-white mx-auto max-w-[8.5in] shadow-lg border border-neutral-200 px-14 py-16 text-neutral-900"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          data-testid="consent-print-document"
        >
          {/* Header */}
          <header className="mb-10 pb-6 border-b border-neutral-300 flex items-start justify-between">
            <div>
              <div
                className="text-[11px] uppercase tracking-[0.32em] text-[#9F5C45]"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                Informed Consent for Services
              </div>
              <h1 className="text-4xl font-normal mt-3 leading-tight">
                Let Go <em className="italic font-light">Wellness</em>
              </h1>
              <p
                className="text-xs text-neutral-600 mt-2"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                Outpatient Mental-Health Services · State of Connecticut
              </p>
            </div>
            <div
              className="text-right text-[10px] text-neutral-600 uppercase tracking-[0.18em]"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              <p>Waterbury, CT</p>
              <p className="mt-1">hello@letgowelllness.com</p>
              <p className="mt-1">letgowelllness.com</p>
            </div>
          </header>

          {/* Patient details block */}
          <section
            className="mb-8 print-section"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            <h2 className="text-[11px] uppercase tracking-[0.32em] text-[#9F5C45] mb-4">
              Patient details
            </h2>
            <div className="grid grid-cols-2 gap-x-8 gap-y-5">
              <FieldLine label="Full legal name" span={2} />
              <FieldLine label="Date of birth" />
              <FieldLine label="Phone" />
              <FieldLine label="Email address" span={2} />
              <FieldLine label="Mailing address" span={2} />
              <FieldLine label="Emergency contact — name" />
              <FieldLine label="Emergency contact — phone" />
              <FieldLine label="Relationship to patient" />
              <FieldLine label="Insurance provider (optional)" />
              <FieldLine label="Insurance member ID (optional)" />
              <FieldLine label="Preferred pronouns" />
            </div>
          </section>

          {/* Consent sections */}
          <section className="space-y-5 mb-10">
            {CONSENT_SECTIONS.map((s, idx) => (
              <div key={s.title} className="print-section">
                <h3
                  className="text-[10px] uppercase tracking-[0.28em] text-[#9F5C45] mb-2"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  {idx + 1}. {s.title}
                </h3>
                <p
                  className="text-[13px] leading-[1.65] text-neutral-800"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  {s.body}
                </p>
              </div>
            ))}
          </section>

          {/* Acknowledgment checkboxes */}
          <section
            className="mb-10 print-section space-y-3 border-t border-neutral-300 pt-6"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            <div className="flex items-start gap-3">
              <div className="w-4 h-4 border border-neutral-800 flex-shrink-0 mt-0.5" />
              <p className="text-[12px] leading-relaxed text-neutral-800">
                <strong>I have read and agree to the terms above</strong> and I consent to
                receive mental-health services from Let Go Wellness. (Required)
              </p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-4 h-4 border border-neutral-800 flex-shrink-0 mt-0.5" />
              <p className="text-[12px] leading-relaxed text-neutral-800">
                I consent to be contacted by Let Go Wellness via email, phone, or text for
                scheduling, appointment reminders, and non-clinical logistics. (Optional)
              </p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-4 h-4 border border-neutral-800 flex-shrink-0 mt-0.5" />
              <p className="text-[12px] leading-relaxed text-neutral-800">
                I have received a copy of the Notice of Privacy Practices (HIPAA). (Required)
              </p>
            </div>
          </section>

          {/* Signatures */}
          <section
            className="print-section border-t border-neutral-800 pt-8"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            <h2 className="text-[11px] uppercase tracking-[0.32em] text-[#9F5C45] mb-6">
              Signatures
            </h2>

            <div className="grid grid-cols-2 gap-x-10 gap-y-8">
              <div>
                <div className="border-b border-neutral-800 h-10" />
                <p className="text-[10px] uppercase tracking-[0.18em] text-neutral-700 mt-2">
                  Patient signature
                </p>
              </div>
              <div>
                <div className="border-b border-neutral-800 h-10" />
                <p className="text-[10px] uppercase tracking-[0.18em] text-neutral-700 mt-2">
                  Date
                </p>
              </div>

              <div>
                <div className="border-b border-neutral-800 h-10" />
                <p className="text-[10px] uppercase tracking-[0.18em] text-neutral-700 mt-2">
                  Patient printed name
                </p>
              </div>
              <div>
                <div className="border-b border-neutral-800 h-10" />
                <p className="text-[10px] uppercase tracking-[0.18em] text-neutral-700 mt-2">
                  Preferred pronouns (optional)
                </p>
              </div>

              <div className="col-span-2 pt-6 mt-2 border-t border-dashed border-neutral-400">
                <p className="text-[10px] uppercase tracking-[0.28em] text-neutral-700 mb-4">
                  If patient is a minor or under conservatorship — parent, legal guardian, or conservator
                </p>
                <div className="grid grid-cols-2 gap-x-10 gap-y-8">
                  <div>
                    <div className="border-b border-neutral-800 h-10" />
                    <p className="text-[10px] uppercase tracking-[0.18em] text-neutral-700 mt-2">
                      Guardian signature
                    </p>
                  </div>
                  <div>
                    <div className="border-b border-neutral-800 h-10" />
                    <p className="text-[10px] uppercase tracking-[0.18em] text-neutral-700 mt-2">
                      Guardian printed name & relationship
                    </p>
                  </div>
                </div>
              </div>

              <div className="col-span-2 pt-6 mt-2 border-t border-dashed border-neutral-400">
                <p className="text-[10px] uppercase tracking-[0.28em] text-neutral-700 mb-4">
                  Clinician acknowledgment
                </p>
                <div className="grid grid-cols-2 gap-x-10 gap-y-8">
                  <div>
                    <div className="border-b border-neutral-800 h-10" />
                    <p className="text-[10px] uppercase tracking-[0.18em] text-neutral-700 mt-2">
                      Clinician signature
                    </p>
                  </div>
                  <div>
                    <div className="border-b border-neutral-800 h-10" />
                    <p className="text-[10px] uppercase tracking-[0.18em] text-neutral-700 mt-2">
                      Date
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <footer
            className="mt-12 pt-4 border-t border-neutral-200 flex items-center justify-between text-[9px] text-neutral-500 tracking-wider uppercase"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            <span>Let Go Wellness · Waterbury, CT</span>
            <span>Retain a signed copy for your records</span>
            <span>letgowelllness.com/consent</span>
          </footer>
        </article>

        <p
          className="no-print max-w-[8.5in] mx-auto text-center text-xs text-neutral-500 mt-6"
          style={{ fontFamily: "'Manrope', sans-serif" }}
        >
          Tip · when the print dialog opens, choose <strong>Save as PDF</strong> as the destination,
          then print the PDF from any device.
        </p>
      </div>
    </>
  );
}
